import { type CSSProperties, useEffect, useMemo, useRef } from "react";

import { cn } from "@/lib/utils";
import type {
  CalendarDate,
  CalendarDayColumn,
  CalendarOccurrence,
  CalendarShellSnapshot,
  CalendarSpanItem,
  CalendarTimedItem,
  CalendarWeekLayout,
} from "../core/shell-types";
import {
  dateKey,
  dayNumberLabel,
  fullDateLabel,
  hourLabel,
  occurrenceColor,
  occurrenceIndex,
  textColorOn,
  timeRangeLabel,
  weekdayShort,
} from "./format";

// One hour of the grid in pixels: 24 rows make the scrollable day; every
// item's top/height is a percentage of that day (minutes / 1440), so the
// hour height only sets the zoom.
const HOUR_PX = 48;
const DAY_PX = HOUR_PX * 24;
const SCROLL_TO_HOUR = 8;
const GUTTER_PX = 56;
const GUTTER_CLASS = "w-14 shrink-0";
const COMPACT_MIN_COLUMN_REM = 6;
const ALL_DAY_ROW_PX = 22;

const HOURS = Array.from({ length: 24 }, (_, hour) => hour);

function DayHeader({
  day,
  compact,
  onSelectDay,
}: {
  day: CalendarDayColumn;
  compact: boolean;
  onSelectDay: (date: CalendarDate) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onSelectDay(day.date)}
      title={`Open ${fullDateLabel(day.date)}`}
      aria-label={fullDateLabel(day.date)}
      aria-current={day.isToday ? "date" : undefined}
      data-date={dateKey(day.date)}
      className={cn(
        "flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 border-l border-border/60 py-1.5 text-center hover:bg-accent/60",
        compact && "min-h-11",
      )}
    >
      <span
        className={cn(
          "text-[11px] uppercase tracking-wider",
          day.isToday ? "font-semibold text-primary" : "text-muted-foreground",
        )}
      >
        {weekdayShort(day.weekday)}
      </span>
      <span
        className={cn(
          "flex h-7 min-w-7 items-center justify-center rounded-full px-1 text-base tabular-nums",
          day.isToday && "bg-primary font-semibold text-primary-foreground",
        )}
      >
        {dayNumberLabel(day.date)}
      </span>
    </button>
  );
}

function AllDayChip({
  item,
  occurrence,
  snapshot,
  columnCount,
  selected,
  onSelect,
}: {
  item: CalendarSpanItem;
  occurrence: CalendarOccurrence;
  snapshot: CalendarShellSnapshot;
  columnCount: number;
  selected: boolean;
  onSelect: (key: string) => void;
}) {
  const color = occurrenceColor(snapshot, occurrence);
  const style: CSSProperties = {
    left: `${(item.startDayIndex / columnCount) * 100}%`,
    width: `${((item.endDayIndex - item.startDayIndex + 1) / columnCount) * 100}%`,
    top: item.row * ALL_DAY_ROW_PX,
    height: ALL_DAY_ROW_PX - 2,
    backgroundColor: color,
    color: textColorOn(color),
  };
  return (
    <button
      type="button"
      style={style}
      data-occurrence-key={item.key}
      title={occurrence.title}
      aria-pressed={selected}
      onClick={() => onSelect(item.key)}
      className={cn(
        "absolute flex min-w-0 items-center truncate px-1.5 text-left text-[11px] font-medium leading-5",
        item.continuesBefore ? "" : "rounded-l",
        item.continuesAfter ? "" : "rounded-r",
        selected && "ring-2 ring-ring ring-offset-1 ring-offset-background",
        occurrence.status === "cancelled" && "line-through opacity-60",
      )}
    >
      <span className="truncate">{occurrence.title || "(untitled)"}</span>
    </button>
  );
}

function TimedBlock({
  item,
  occurrence,
  snapshot,
  selected,
  onSelect,
}: {
  item: CalendarTimedItem;
  occurrence: CalendarOccurrence;
  snapshot: CalendarShellSnapshot;
  selected: boolean;
  onSelect: (key: string) => void;
}) {
  const color = occurrenceColor(snapshot, occurrence);
  const durationMinutes = item.endMinute - item.startMinute;
  const style: CSSProperties = {
    top: `${(item.startMinute / 1440) * 100}%`,
    height: `${(durationMinutes / 1440) * 100}%`,
    left: `calc(${(item.column / item.columnCount) * 100}% + 1px)`,
    width: `calc(${(1 / item.columnCount) * 100}% - 3px)`,
    backgroundColor: color,
    color: textColorOn(color),
  };
  const timeText = timeRangeLabel(occurrence.start, occurrence.end, snapshot.timeZone);
  const showTime = durationMinutes >= 40;
  return (
    <button
      type="button"
      style={style}
      data-occurrence-key={item.key}
      title={`${occurrence.title}\n${timeText}`}
      aria-label={`${occurrence.title || "(untitled)"}, ${timeText}`}
      aria-pressed={selected}
      onClick={() => onSelect(item.key)}
      className={cn(
        "absolute z-10 flex min-w-0 flex-col overflow-hidden px-1.5 py-0.5 text-left text-[11px] leading-tight shadow-sm",
        item.continuesFromPreviousDay
          ? "rounded-t-none border-t border-dashed border-white/60"
          : "rounded-t-md",
        item.continuesIntoNextDay
          ? "rounded-b-none border-b border-dashed border-white/60"
          : "rounded-b-md",
        selected && "ring-2 ring-ring ring-offset-1 ring-offset-background",
        occurrence.status === "cancelled" && "line-through opacity-60",
        occurrence.status === "tentative" && "opacity-80",
      )}
    >
      <span className="truncate font-medium">{occurrence.title || "(untitled)"}</span>
      {showTime ? <span className="truncate opacity-90">{timeText}</span> : null}
      {occurrence.locationSummary && durationMinutes >= 75 ? (
        <span className="truncate opacity-80">{occurrence.locationSummary}</span>
      ) : null}
    </button>
  );
}

export function TimeGridView({
  snapshot,
  layout,
  compact,
  onSelectDay,
  onSelectOccurrence,
}: {
  snapshot: CalendarShellSnapshot;
  layout: CalendarWeekLayout;
  compact: boolean;
  onSelectDay: (date: CalendarDate) => void;
  onSelectOccurrence: (key: string) => void;
}) {
  const index = useMemo(() => occurrenceIndex(snapshot), [snapshot]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const columnCount = Math.max(layout.days.length, 1);
  const isWeek = layout.days.length > 1;
  const viewName = isWeek ? "week" : "day";
  const scrollHorizontally = compact && isWeek;
  const minWidth = scrollHorizontally
    ? `${COMPACT_MIN_COLUMN_REM * columnCount + 3.5}rem`
    : undefined;

  // One scroll container for both axes so the day header can stick to the
  // top and the hour gutter to the left (compact week scrolls sideways).
  // Start the day at working hours whenever the period changes, and bring
  // today's column into view on a phone-width week.
  const headerRef = useRef<HTMLDivElement>(null);
  const firstDayKey = layout.days[0] ? dateKey(layout.days[0].date) : "";
  useEffect(() => {
    const element = scrollRef.current;
    if (!element || !firstDayKey) {
      return;
    }
    // The header is sticky, so the body's hour 8 sits right under it when the
    // container has scrolled by that hour's offset alone.
    element.scrollTop = SCROLL_TO_HOUR * HOUR_PX - 8;
    if (scrollHorizontally) {
      const today = headerRef.current?.querySelector<HTMLElement>('[aria-current="date"]');
      element.scrollLeft = today ? Math.max(0, today.offsetLeft - GUTTER_PX) : 0;
    }
  }, [firstDayKey, scrollHorizontally]);

  const allDayHeight = Math.max(layout.allDayRowCount, 0) * ALL_DAY_ROW_PX;

  return (
    <section
      data-calendar-view={viewName}
      className="flex min-h-0 flex-1 flex-col"
      aria-label={isWeek ? "Week grid" : "Day grid"}
    >
      <div
        ref={scrollRef}
        className={cn(
          "min-h-0 flex-1 overflow-y-auto",
          scrollHorizontally ? "overflow-x-auto" : "overflow-x-hidden",
        )}
      >
        <div style={{ minWidth }}>
          <div
            ref={headerRef}
            className="sticky top-0 z-30 border-b border-border/70 bg-background"
          >
            <div className="flex">
              <div className={cn(GUTTER_CLASS, "sticky left-0 z-10 bg-background")} />
              {layout.days.map((day) => (
                <DayHeader
                  key={dateKey(day.date)}
                  day={day}
                  compact={compact}
                  onSelectDay={onSelectDay}
                />
              ))}
            </div>
            {layout.allDayRowCount > 0 || layout.allDayItems.length > 0 ? (
              <section className="flex border-t border-border/60" aria-label="All-day events">
                <div
                  className={cn(
                    GUTTER_CLASS,
                    "sticky left-0 z-10 bg-background py-1 pr-2 text-right text-[10px] uppercase tracking-wider text-muted-foreground",
                  )}
                >
                  All day
                </div>
                <div className="relative min-w-0 flex-1" style={{ height: allDayHeight + 4 }}>
                  {layout.days.map((day, dayIndex) => (
                    <div
                      key={dateKey(day.date)}
                      aria-hidden
                      className="absolute inset-y-0 border-l border-border/60"
                      style={{ left: `${(dayIndex / columnCount) * 100}%` }}
                    />
                  ))}
                  {layout.allDayItems.map((item) => {
                    const occurrence = index.get(item.key);
                    if (!occurrence) {
                      return null;
                    }
                    return (
                      <AllDayChip
                        key={item.key}
                        item={item}
                        occurrence={occurrence}
                        snapshot={snapshot}
                        columnCount={columnCount}
                        selected={snapshot.selectedOccurrenceKey === item.key}
                        onSelect={onSelectOccurrence}
                      />
                    );
                  })}
                </div>
              </section>
            ) : null}
          </div>
          <div className="relative flex" style={{ height: DAY_PX }}>
            <div className={cn(GUTTER_CLASS, "sticky left-0 z-10 bg-background")} aria-hidden>
              {HOURS.map((hour) => (
                <div
                  key={hour}
                  className="absolute right-2 -translate-y-1/2 text-[10px] tabular-nums text-muted-foreground"
                  style={{ top: hour * HOUR_PX }}
                >
                  {hour === 0 ? "" : hourLabel(hour)}
                </div>
              ))}
            </div>
            <div className="relative flex min-w-0 flex-1" role="presentation">
              <div aria-hidden className="pointer-events-none absolute inset-0">
                {HOURS.map((hour) => (
                  <div
                    key={hour}
                    className="absolute inset-x-0 border-t border-border/50"
                    style={{ top: hour * HOUR_PX }}
                  />
                ))}
              </div>
              {layout.days.map((day, dayIndex) => (
                <div
                  key={dateKey(day.date)}
                  data-date={dateKey(day.date)}
                  className={cn(
                    "relative min-w-0 flex-1 border-l border-border/60",
                    day.isToday && "bg-primary/[0.03]",
                  )}
                >
                  {day.timedItems.map((item) => {
                    const occurrence = index.get(item.key);
                    if (!occurrence) {
                      return null;
                    }
                    return (
                      <TimedBlock
                        key={item.key}
                        item={item}
                        occurrence={occurrence}
                        snapshot={snapshot}
                        selected={snapshot.selectedOccurrenceKey === item.key}
                        onSelect={onSelectOccurrence}
                      />
                    );
                  })}
                  {layout.nowIndicator && layout.nowIndicator.dayIndex === dayIndex ? (
                    <div
                      aria-hidden
                      data-now-indicator
                      className="pointer-events-none absolute inset-x-0 z-20 flex items-center"
                      style={{ top: `${(layout.nowIndicator.minute / 1440) * 100}%` }}
                    >
                      <span className="-ml-1 h-2 w-2 rounded-full bg-red-500" />
                      <span className="h-px flex-1 bg-red-500" />
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
